/**
 * Sim Mode host — `/:projectId/sim` (kiosk hosts only, behind operator auth).
 *
 * Renders ANY booth page (default views + every custom path: bahela, bahelakotak,
 * newspaper-*, samu, ...) against a synthetic session state, the project's REAL
 * prod config/catalog, and a simulated viewport — without walking the linear flow.
 *
 * Everything is driven by URL params, so states are deep-linkable (shareable and
 * screenshotable by a headless browser / AI agent):
 *
 *   /:projectId/sim?page=checkout&path=bahela&scenario=paid&vw=1080x1920
 *
 * Params:
 *   page      welcome|tutorial|camera|checkout|payment|filter|frame|capture|
 *             record|retake|select|edit|processing|result|post-session-ai
 *   path      customPath override ('default' forces the default page set;
 *             omitted = the project's configured customPath)
 *   scenario  see simScenarios.ts (omitted = per-page default)
 *   state     base64url(JSON) DeepPartial<SessionStateData> merged over the scenario
 *   cfg       base64url(JSON) deep-partial project config merged over the real config
 *   vw        WxH (e.g. 1080x1920) or preset: booth-portrait|booth-landscape|
 *             ipad-portrait|ipad-landscape|tablet-wide|mobile (omitted = design canvas)
 *   expiresIn payment expiry in seconds from now (payment scenarios)
 *   photos    captured-photo count override (layout density)
 *   filters   catalog shaping: cycle/clone the filter catalog to exactly N
 *   frames    catalog shaping: cycle/clone the frame catalog to exactly N
 *   frameAspect WxH — put a synthetic frame with this print canvas FIRST in the
 *             catalog (drives photo ratio + strip shape); census presets in panel
 *   stripAspect WxH — override only the result strip placeholder's canvas
 *   cam       1 connects the default webcam for live preview/capture
 *   panel     0 hides the control panel (screenshots)
 *
 * Entering sim enables the sticky read-only guard (see lib/simulation.ts). Pages
 * that navigate (countdown ends, Continue buttons) are recaptured back into
 * `/sim?page=<target>` by SimFlowRecapture, so you can also click through the
 * flow — all writes stay blocked. Exit = full reload via the panel button.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { Loading } from '@renderer/components'
import ScaleToFit from '@renderer/components/ScaleToFit'
import { ResponsiveTierProvider } from '@renderer/context/ResponsiveTierContext'
import { useConfigStore } from '@renderer/stores/configState'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { getCustomRoutes, CUSTOM_ROUTE_KEYS } from '@renderer/pages/view/customRoutes'
import PostSessionAIPage from '@renderer/pages/view/PostSessionAI'
import { enableSimulationMode, isSimulationActive } from '@renderer/lib/simulation'
import { ensureCameraPermission } from '@renderer/lib/runtime'
import { connectDigitalCamera } from '@renderer/lib/digitalCamera'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { useDeviceOrientation } from '@renderer/hooks/useDeviceOrientation'
import { getDesignCanvasSizeForOrientation } from '@renderer/utils/designCanvasSize'
import type { ConfigState } from '@renderer/types/configState'
import type { SessionStateData, DeepPartial } from '@renderer/types/sessionState'
import {
  buildScenarioState,
  shapeCatalog,
  DEFAULT_SCENARIO_BY_PAGE,
  SCENARIO_NAMES,
  type ScenarioName
} from './simScenarios'
import type { Filter, FrameOption } from '@renderer/types/configState'

const PAGES = [
  'welcome',
  'mode-select',
  'tutorial',
  'camera',
  'checkout',
  'payment',
  'filter',
  'frame',
  'capture',
  'retake',
  'select',
  'edit',
  'processing',
  'result',
  'post-session-ai'
] as const

/**
 * Print-canvas presets for catalog shaping, from the 2026-07 prod frame census
 * (ui-audit/census): 4R portrait alone is 92% of all frames; these 6 classes
 * cover 98%+. Values are representative real pixel dims per class.
 */
const FRAME_ASPECT_PRESETS: Array<[string, string]> = [
  ['4R portrait', '1200x1800'],
  ['A-series/5x7', '2480x3508'],
  ['2R strip', '600x1800'],
  ['4R landscape', '1600x1067'],
  ['tall portrait', '1013x1800'],
  ['panoramic 6x20', '1800x6000']
]

const VIEWPORT_PRESETS: Record<string, { width: number; height: number }> = {
  'booth-portrait': { width: 1080, height: 1920 },
  'booth-landscape': { width: 1920, height: 1080 },
  'ipad-portrait': { width: 768, height: 1024 },
  'ipad-landscape': { width: 1024, height: 768 },
  'tablet-wide': { width: 1280, height: 800 },
  mobile: { width: 390, height: 844 }
}

/**
 * Last sim search params, so SimFlowRecapture can restore them when a page
 * navigates into the real flow and we pull it back into /sim.
 */
let lastSimSearch = ''

/** The pristine (un-patched) project config as loaded by ProjectRoute. */
let pristineConfig: ConfigState | null = null
let pristineConfigId: string | null = null

/** Pristine catalog (filters/frames as loaded by ProjectRoute), pre-shaping. */
let pristineFilters: Filter[] | null = null
let pristineFrames: FrameOption[] | null = null
let pristineCatalogId: string | null = null

/** Parse "WxH" (e.g. 1200x1800) → dims, or null. */
function parseDims(raw: string): { width: number; height: number } | null {
  const m = /^(\d+)x(\d+)$/.exec(raw)
  if (!m) return null
  const width = parseInt(m[1], 10)
  const height = parseInt(m[2], 10)
  if (!width || !height) return null
  return { width, height }
}

// ---------------------------------------------------------------------------

/** Inverse of parseB64Json: JSON value → base64url for the `state`/`cfg` params. */
function toB64Json(value: unknown): string {
  const json = JSON.stringify(value)
  const utf8 = encodeURIComponent(json).replace(/%([0-9A-F]{2})/g, (_m, hex: string) =>
    String.fromCharCode(parseInt(hex, 16))
  )
  return btoa(utf8).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function parseB64Json<T>(raw: string | null): T | null {
  if (!raw) return null
  try {
    const b64 = raw.replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(b64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(json) as T
  } catch (e) {
    console.warn('[SIM] Failed to parse base64 JSON param:', e)
    return null
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && !(v instanceof Date)
}

/** Recursively merge `patch` into `target` (arrays and scalars replace). */
function deepMerge<T>(target: T, patch: unknown): T {
  if (!isPlainObject(target) || !isPlainObject(patch)) {
    return (patch === undefined ? target : (patch as T)) as T
  }
  const out: Record<string, unknown> = { ...target }
  for (const [key, value] of Object.entries(patch)) {
    out[key] = isPlainObject(value) && isPlainObject(out[key]) ? deepMerge(out[key], value) : value
  }
  return out as T
}

/** Read a dot-path value from a nested object (undefined when absent). */
function getPath(obj: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>((acc, key) => (isPlainObject(acc) ? acc[key] : undefined), obj)
}

/** Immutably set a dot-path value, creating intermediate objects as needed. */
function setPath(
  obj: Record<string, unknown>,
  path: string,
  value: unknown
): Record<string, unknown> {
  const keys = path.split('.')
  const out = { ...obj }
  let cur: Record<string, unknown> = out
  keys.forEach((k, i) => {
    if (i === keys.length - 1) {
      cur[k] = value
      return
    }
    const next = isPlainObject(cur[k]) ? { ...(cur[k] as Record<string, unknown>) } : {}
    cur[k] = next
    cur = next
  })
  return out
}

/**
 * Frequently-tuned numeric config knobs surfaced as quick fields in the panel.
 * Everything else is reachable through the raw JSON patch textarea — both feed
 * the same `cfg` URL param, so all overrides stay temporary and shareable.
 */
const QUICK_CFG_FIELDS: Array<{ label: string; path: string }> = [
  { label: 'Capture count', path: 'pages.capture.settings.captureCount' },
  { label: 'Capture countdown (s)', path: 'pages.capture.settings.captureCountdown' },
  { label: 'Max retake count', path: 'pages.retake.settings.maxRetakeCount' },
  { label: 'Max retake sessions', path: 'pages.retake.settings.maxRetakeSession' },
  { label: 'Idle timeout (s)', path: 'theme.idleTimeout' }
]

/**
 * Recapture wrapper for the real View route: once sim is active, any navigation
 * a page performs (e.g. `navigate('/<projectId>/payment')` after a countdown)
 * is redirected back into `/sim?page=payment` with the previous sim params, so
 * the flow stays inside the harness and the seeded session never runs the real
 * View chrome (camera monitors, credit modals, telemetry).
 */
export const SimFlowRecapture: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { projectId } = useParams()
  const location = useLocation()

  if (!isSimulationActive() || !projectId) {
    return children
  }

  const segments = location.pathname.split('/').filter(Boolean)
  const last = segments[segments.length - 1]
  const page = last && last !== projectId && (PAGES as readonly string[]).includes(last) ? last : 'welcome'
  const params = new URLSearchParams(lastSimSearch)
  params.set('page', page)
  return <Navigate to={`/${projectId}/sim?${params.toString()}`} replace />
}

// ---------------------------------------------------------------------------

const SimBadge: React.FC = () => (
  <div className="pointer-events-none fixed left-1 top-1 z-[9999] rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white opacity-80">
    Sim
  </div>
)

interface PanelProps {
  page: string
  pathParam: string
  scenarioParam: string
  vwParam: string
  expiresInParam: string
  photosParam: string
  filtersParam: string
  framesParam: string
  frameAspectParam: string
  stripAspectParam: string
  camOn: boolean
  cfgParam: string | null
  effectiveConfig: ConfigState | null
  effectiveViewport: { width: number; height: number }
  onUpdate: (updates: Record<string, string | null>) => void
  onReseed: () => void
  projectId: string
}

const SimPanel: React.FC<PanelProps> = ({
  page,
  pathParam,
  scenarioParam,
  vwParam,
  expiresInParam,
  photosParam,
  filtersParam,
  framesParam,
  frameAspectParam,
  stripAspectParam,
  camOn,
  cfgParam,
  effectiveConfig,
  effectiveViewport,
  onUpdate,
  onReseed,
  projectId
}) => {
  const [open, setOpen] = useState(true)
  const [copied, setCopied] = useState(false)
  const [cfgCopied, setCfgCopied] = useState(false)
  const [cfgError, setCfgError] = useState<string | null>(null)
  // The config-patch draft (JSON text). Source of truth for the config section;
  // quick fields rewrite it, Apply pushes it into the `cfg` URL param.
  const [cfgText, setCfgText] = useState<string>(() => {
    const parsed = parseB64Json<Record<string, unknown>>(cfgParam)
    return parsed ? JSON.stringify(parsed, null, 2) : ''
  })

  const cfgDraft = useMemo((): Record<string, unknown> | null => {
    if (!cfgText.trim()) return {}
    try {
      const parsed = JSON.parse(cfgText) as unknown
      return isPlainObject(parsed) ? parsed : null
    } catch {
      return null
    }
  }, [cfgText])

  const setQuickField = (path: string, raw: string): void => {
    const base = cfgDraft ?? {}
    const num = Number(raw)
    const next =
      raw.trim() === '' || Number.isNaN(num) ? setPath(base, path, undefined) : setPath(base, path, num)
    setCfgText(JSON.stringify(next, null, 2))
  }

  const applyCfg = (): void => {
    if (cfgDraft === null) {
      setCfgError('Invalid JSON')
      return
    }
    setCfgError(null)
    const cleaned = JSON.parse(JSON.stringify(cfgDraft)) as Record<string, unknown> // drop undefined
    const empty = Object.keys(cleaned).length === 0
    onUpdate({ cfg: empty ? null : toB64Json(cleaned) })
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed right-2 top-2 z-[9999] rounded bg-gray-900 px-2 py-1 text-xs font-bold text-white opacity-80 shadow"
      >
        SIM ⚙
      </button>
    )
  }

  const selectCls =
    'w-full rounded border border-gray-600 bg-gray-800 px-1.5 py-1 text-xs text-white'
  const labelCls = 'mb-0.5 mt-2 block text-[10px] font-semibold uppercase tracking-wide text-gray-400'

  return (
    <div
      data-operator-area
      className="fixed right-2 top-2 z-[9999] max-h-[calc(100dvh-1rem)] w-56 overflow-y-auto rounded-lg bg-gray-900 p-3 text-white shadow-xl"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-red-400">Sim Mode</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded px-1 text-xs text-gray-400 hover:bg-gray-700"
        >
          minimize
        </button>
      </div>

      <label className={labelCls}>Page</label>
      <select className={selectCls} value={page} onChange={(e) => onUpdate({ page: e.target.value })}>
        {PAGES.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
      </select>

      <label className={labelCls}>Custom path</label>
      <select
        className={selectCls}
        value={pathParam}
        onChange={(e) => onUpdate({ path: e.target.value || null })}
      >
        <option value="">(project default)</option>
        <option value="default">default (force)</option>
        {CUSTOM_ROUTE_KEYS.map((k) => (
          <option key={k} value={k}>
            {k}
          </option>
        ))}
      </select>

      <label className={labelCls}>Scenario</label>
      <select
        className={selectCls}
        value={scenarioParam}
        onChange={(e) => onUpdate({ scenario: e.target.value || null })}
      >
        <option value="">auto ({DEFAULT_SCENARIO_BY_PAGE[page] || 'started'})</option>
        {SCENARIO_NAMES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>

      <label className={labelCls}>Viewport</label>
      <select
        className={selectCls}
        value={vwParam in VIEWPORT_PRESETS || vwParam === '' ? vwParam : 'custom'}
        onChange={(e) => {
          const v = e.target.value
          if (v === 'custom') return
          onUpdate({ vw: v || null })
        }}
      >
        <option value="">design canvas ({effectiveViewport.width}×{effectiveViewport.height})</option>
        {Object.entries(VIEWPORT_PRESETS).map(([k, v]) => (
          <option key={k} value={k}>
            {k} ({v.width}×{v.height})
          </option>
        ))}
        <option value="custom">custom (edit ?vw=WxH)</option>
      </select>

      <label className={labelCls}>Payment expires in (s)</label>
      <input
        className={selectCls}
        type="number"
        placeholder="300"
        value={expiresInParam}
        onChange={(e) => onUpdate({ expiresIn: e.target.value || null })}
      />

      <label className={labelCls}>Photos (captured count)</label>
      <input
        className={selectCls}
        type="number"
        min={1}
        max={24}
        placeholder="frame slots"
        value={photosParam}
        onChange={(e) => onUpdate({ photos: e.target.value || null })}
      />

      <details className="mt-2" open={Boolean(filtersParam || framesParam || frameAspectParam || stripAspectParam)}>
        <summary className="cursor-pointer text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          Catalog shaping
        </summary>
        <label className={labelCls}>Filters (count)</label>
        <input
          className={selectCls}
          type="number"
          min={0}
          max={48}
          placeholder="project catalog"
          value={filtersParam}
          onChange={(e) => onUpdate({ filters: e.target.value || null })}
        />
        <label className={labelCls}>Frames (count)</label>
        <input
          className={selectCls}
          type="number"
          min={0}
          max={48}
          placeholder="project catalog"
          value={framesParam}
          onChange={(e) => onUpdate({ frames: e.target.value || null })}
        />
        <label className={labelCls}>Primary frame canvas (WxH)</label>
        <select
          className={selectCls}
          value={frameAspectParam}
          onChange={(e) => onUpdate({ frameAspect: e.target.value || null })}
        >
          <option value="">(project frames)</option>
          {FRAME_ASPECT_PRESETS.map(([label, dims]) => (
            <option key={dims} value={dims}>
              {label} ({dims})
            </option>
          ))}
        </select>
        <label className={labelCls}>Strip canvas (WxH)</label>
        <select
          className={selectCls}
          value={stripAspectParam}
          onChange={(e) => onUpdate({ stripAspect: e.target.value || null })}
        >
          <option value="">(primary frame)</option>
          {FRAME_ASPECT_PRESETS.map(([label, dims]) => (
            <option key={dims} value={dims}>
              {label} ({dims})
            </option>
          ))}
        </select>
      </details>

      <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={camOn}
          onChange={(e) => onUpdate({ cam: e.target.checked ? '1' : null })}
        />
        <span>Live webcam (preview/capture)</span>
      </label>

      <details className="mt-2">
        <summary className="cursor-pointer text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          Config overrides (temp)
        </summary>
        <div className="mt-1">
          {QUICK_CFG_FIELDS.map(({ label, path }) => {
            const draftVal = cfgDraft ? getPath(cfgDraft, path) : undefined
            const effectiveVal = getPath(effectiveConfig, path)
            return (
              <React.Fragment key={path}>
                <label className={labelCls}>{label}</label>
                <input
                  className={selectCls}
                  type="number"
                  placeholder={effectiveVal === undefined ? '—' : String(effectiveVal)}
                  value={draftVal === undefined || draftVal === null ? '' : String(draftVal)}
                  onChange={(e) => setQuickField(path, e.target.value)}
                />
              </React.Fragment>
            )
          })}

          <label className={labelCls}>Raw JSON patch (deep-merged)</label>
          <textarea
            className={`${selectCls} h-24 resize-y font-mono text-[10px] leading-tight`}
            placeholder={'{\n  "theme": { "idleTimeout": 5 }\n}'}
            value={cfgText}
            onChange={(e) => setCfgText(e.target.value)}
            spellCheck={false}
          />
          {(cfgError || cfgDraft === null) && (
            <p className="mt-0.5 text-[10px] text-red-400">{cfgError || 'Invalid JSON'}</p>
          )}

          <div className="mt-1.5 flex gap-1.5">
            <button
              type="button"
              onClick={applyCfg}
              className="flex-1 rounded bg-blue-600 px-2 py-1 text-xs font-semibold hover:bg-blue-500"
            >
              Apply config
            </button>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(JSON.stringify(effectiveConfig, null, 2))
                  .then(() => {
                    setCfgCopied(true)
                    setTimeout(() => setCfgCopied(false), 1500)
                  })
              }}
              className="flex-1 rounded bg-gray-700 px-2 py-1 text-xs font-semibold hover:bg-gray-600"
            >
              {cfgCopied ? 'Copied!' : 'Copy config'}
            </button>
          </div>
          <p className="mt-1 text-[10px] leading-snug text-gray-500">
            Overrides live in the URL only — the saved project config is never touched.
          </p>
        </div>
      </details>

      <div className="mt-3 flex gap-1.5">
        <button
          type="button"
          onClick={onReseed}
          className="flex-1 rounded bg-blue-600 px-2 py-1 text-xs font-semibold hover:bg-blue-500"
        >
          Re-seed
        </button>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(window.location.href).then(() => {
              setCopied(true)
              setTimeout(() => setCopied(false), 1500)
            })
          }}
          className="flex-1 rounded bg-gray-700 px-2 py-1 text-xs font-semibold hover:bg-gray-600"
        >
          {copied ? 'Copied!' : 'Copy URL'}
        </button>
      </div>
      <button
        type="button"
        onClick={() => window.location.assign(`/${projectId}`)}
        className="mt-1.5 w-full rounded bg-red-700 px-2 py-1 text-xs font-semibold hover:bg-red-600"
      >
        Exit sim (reload)
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------

const SimHost: React.FC = () => {
  // Flip the read-only guard on before ANY child renders or effect runs.
  enableSimulationMode()

  const { projectId: routeParam } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { isPortrait: deviceIsPortrait } = useDeviceOrientation()

  const config = useConfigStore((s) => s.config)

  const page = searchParams.get('page') || 'welcome'
  const pathParam = searchParams.get('path') || ''
  const scenarioParam = searchParams.get('scenario') || ''
  const stateParam = searchParams.get('state')
  const cfgParam = searchParams.get('cfg')
  const vwParam = searchParams.get('vw') || ''
  const expiresInParam = searchParams.get('expiresIn') || ''
  const photosParam = searchParams.get('photos') || ''
  const camOn = searchParams.get('cam') === '1'
  // `embedded=1` marks the INNER instance rendered inside the fidelity iframe (see
  // the render below): it draws only the booth page — no control panel, no scaling
  // frame — because the iframe's own viewport already equals the design canvas, so
  // `dvh`/`vh` inside resolve to the canvas regardless of the outer window/zoom.
  const embedded = searchParams.get('embedded') === '1'
  const showPanel = !embedded && searchParams.get('panel') !== '0'
  // Catalog shaping: filter/frame density and aspect fixtures. These vary what
  // the project's CATALOG looks like — the config (`cfg`) can't reach them.
  const filtersParam = searchParams.get('filters') || ''
  const framesParam = searchParams.get('frames') || ''
  const frameAspectParam = searchParams.get('frameAspect') || ''
  const stripAspectParam = searchParams.get('stripAspect') || ''

  const scenario: ScenarioName = (SCENARIO_NAMES as readonly string[]).includes(scenarioParam)
    ? (scenarioParam as ScenarioName)
    : DEFAULT_SCENARIO_BY_PAGE[page] || 'started'

  // Keep the recapture params fresh.
  useEffect(() => {
    lastSimSearch = searchParams.toString()
  }, [searchParams])

  // --- 1. Config + catalog: snapshot pristine once, re-apply patches/shaping. -
  const configKey = `${pathParam}|${cfgParam ?? ''}|${filtersParam}|${framesParam}|${frameAspectParam}`
  const [appliedConfigKey, setAppliedConfigKey] = useState<string | null>(null)
  useEffect(() => {
    const store = useConfigStore.getState()
    const current = store.config
    if (!current) return
    if (!pristineConfig || pristineConfigId !== current._id) {
      pristineConfig = JSON.parse(JSON.stringify(current)) as ConfigState
      pristineConfigId = current._id ?? null
    }

    let patched = JSON.parse(JSON.stringify(pristineConfig)) as ConfigState
    if (pathParam) {
      patched.pages.welcome.settings.customPath = pathParam === 'default' ? null : pathParam
    }
    const cfgPatch = parseB64Json<DeepPartial<ConfigState>>(cfgParam)
    if (cfgPatch) {
      patched = deepMerge(patched, cfgPatch)
    }
    store.setConfig(patched)

    // Catalog shaping — snapshot the pristine catalog once per project, then
    // derive the shaped view from it every time (so removing a param restores
    // the real catalog). setFilters is async (thumbnail/LUT bookkeeping); the
    // seed effect only needs the arrays, which are set synchronously enough —
    // we still gate the seed on appliedConfigKey AFTER this completes.
    if (!pristineFilters || pristineCatalogId !== (current._id ?? null)) {
      pristineFilters = store.filters.slice()
      pristineFrames = store.frames.slice()
      pristineCatalogId = current._id ?? null
    }
    const filterCount = parseInt(filtersParam, 10)
    const frameCount = parseInt(framesParam, 10)
    const frameAspect = parseDims(frameAspectParam)
    const shaped = shapeCatalog(pristineFilters ?? [], pristineFrames ?? [], {
      filterCount: Number.isFinite(filterCount) ? filterCount : undefined,
      frameCount: Number.isFinite(frameCount) ? frameCount : undefined,
      frameAspect: frameAspect ?? undefined
    })
    store.setFrames(shaped.frames)
    void store
      .setFilters(shaped.filters)
      .catch((e) => console.warn('[SIM] setFilters during catalog shaping failed:', e))
      .finally(() => setAppliedConfigKey(configKey))
  }, [configKey, pathParam, cfgParam, filtersParam, framesParam, frameAspectParam])

  // --- 2. Session: seed the scenario state (after config is applied). --------
  const [seedNonce, setSeedNonce] = useState(0)
  const seedKey = `${scenario}|${stateParam ?? ''}|${expiresInParam}|${photosParam}|${stripAspectParam}|${configKey}|${seedNonce}`
  const [appliedSeedKey, setAppliedSeedKey] = useState<string | null>(null)
  useEffect(() => {
    if (!embedded) return // only the inner instance renders the page + session state
    if (appliedConfigKey !== configKey) return
    const { config: cfg, frames, filters } = useConfigStore.getState()
    if (!cfg) return

    const expiresIn = Number.parseInt(expiresInParam, 10)
    const photoCount = Number.parseInt(photosParam, 10)
    let seeded = buildScenarioState(
      scenario,
      {
        projectId: cfg._id || routeParam || '',
        userId: useSessionStateStore.getState().userId || 'sim-user',
        config: cfg,
        frames,
        filters
      },
      {
        expiresIn: Number.isFinite(expiresIn) ? expiresIn : undefined,
        photoCount: Number.isFinite(photoCount) ? photoCount : undefined,
        stripAspect: parseDims(stripAspectParam) ?? undefined
      }
    )
    const statePatch = parseB64Json<DeepPartial<SessionStateData>>(stateParam)
    if (statePatch) {
      seeded = deepMerge(seeded, statePatch)
    }
    useSessionStateStore.setState(seeded)
    setAppliedSeedKey(seedKey)
    console.log(`[SIM] Seeded scenario "${scenario}" for page "${page}"`, seeded)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey, appliedConfigKey])

  // --- 2b. Optional live webcam (?cam=1): real preview/capture experience. ----
  // Reuses the digital-mode webcam path (default browser camera into the camera
  // store) — pages then render a live Preview exactly like a web booth would.
  // DSLR/USB tethering is out of scope in a browser tab; this is webcam-only.
  const camConnectedBySim = useRef(false)
  useEffect(() => {
    if (!embedded) return undefined // camera lives with the page, in the inner instance only
    if (camOn) {
      let cancelled = false
      void (async (): Promise<void> => {
        try {
          await ensureCameraPermission()
          const ok = await connectDigitalCamera()
          if (ok && !cancelled) {
            camConnectedBySim.current = true
            console.log('[SIM] Live webcam connected')
          }
        } catch (err) {
          console.warn('[SIM] Live webcam connect failed:', err)
        }
      })()
      return (): void => {
        cancelled = true
      }
    }
    // Toggled off: tear down only a webcam WE connected.
    if (camConnectedBySim.current) {
      camConnectedBySim.current = false
      const store = useCameraStore.getState()
      const webcam = store.webcam
      if (webcam) {
        void Promise.resolve(webcam.disconnect()).catch(() => {})
        store.setWebcam(null)
      }
    }
    return undefined
  }, [camOn])

  // --- 3. Resolve page component + viewport. ---------------------------------
  const effectiveCustomPath = config?.pages?.welcome?.settings?.customPath || undefined
  const pageComponents = useMemo(() => getCustomRoutes(effectiveCustomPath), [effectiveCustomPath])

  const PageComponent: React.ComponentType | undefined = useMemo(() => {
    switch (page) {
      case 'welcome':
        return pageComponents.Welcome
      case 'mode-select':
        // Hub picker. Pass members via `cfg` (DeepPartial config merge), e.g.
        // cfg=b64({pages:{modeSelect:{settings:{enabled:true,members:[...]}}}}).
        return pageComponents.ModeSelect
      case 'tutorial':
        return pageComponents.Tutorial
      case 'camera':
        return pageComponents.Camera
      case 'checkout':
        return pageComponents.Checkout
      case 'payment':
        return pageComponents.Payment
      case 'filter':
        return pageComponents.Filter
      case 'frame':
        return pageComponents.Frame
      case 'capture':
        return pageComponents.Capture
      case 'retake':
        return pageComponents.Retake
      case 'select':
        return pageComponents.Select
      case 'edit':
        return pageComponents.Edit
      case 'processing':
        return pageComponents.Processing
      case 'result':
        return pageComponents.Result
      case 'post-session-ai':
        return PostSessionAIPage
      default:
        return pageComponents.Welcome
    }
  }, [page, pageComponents])

  const viewport = useMemo(() => {
    if (VIEWPORT_PRESETS[vwParam]) return VIEWPORT_PRESETS[vwParam]
    const match = /^(\d+)x(\d+)$/.exec(vwParam)
    if (match) {
      return { width: parseInt(match[1], 10), height: parseInt(match[2], 10) }
    }
    if (config) {
      const design = getDesignCanvasSizeForOrientation(
        config,
        deviceIsPortrait ? 'portrait' : 'landscape'
      )
      if (design) return { width: design.width, height: design.height }
      return { width: config.screenSize.width, height: config.screenSize.height }
    }
    return { width: 1080, height: 1920 }
  }, [vwParam, config, deviceIsPortrait])

  const update = (updates: Record<string, string | null>): void => {
    const next = new URLSearchParams(searchParams)
    for (const [k, v] of Object.entries(updates)) {
      if (v === null || v === '') next.delete(k)
      else next.set(k, v)
    }
    setSearchParams(next, { replace: true })
  }

  // The inner (embedded) URL: same state, but rendered page-only inside the iframe.
  const embeddedSrc = useMemo(() => {
    const p = new URLSearchParams(searchParams)
    p.set('embedded', '1')
    p.delete('panel')
    return `${window.location.pathname}?${p.toString()}`
  }, [searchParams])

  const ready = Boolean(config) && appliedSeedKey === seedKey

  // INNER instance — render ONLY the booth page. The hosting iframe's viewport is
  // exactly `viewport`, so ScaleToFit resolves to scale ≈ 1 and every `dvh`/`vh`
  // inside the page maps to the design canvas. That is the whole point of the
  // iframe: viewport units stay faithful regardless of the outer window or zoom.
  if (embedded) {
    return (
      <div className="relative w-screen overflow-hidden bg-[#101010]" style={{ height: '100dvh' }}>
        <ResponsiveTierProvider virtualViewport={viewport}>
          <div className="relative h-full min-h-0 w-full min-w-0 overflow-hidden">
            <ScaleToFit
              screenSize={viewport}
              backgroundColor={config?.theme?.backgroundColor || 'white'}
              blurBackgroundColor="#1D1D1D"
            >
              <div className="relative flex h-full min-h-0 w-full flex-col">
                {!ready ? (
                  <Loading />
                ) : PageComponent ? (
                  <PageComponent key={`${effectiveCustomPath ?? 'default'}|${page}|${appliedSeedKey}`} />
                ) : (
                  <div className="flex h-full items-center justify-center text-gray-500">
                    Page “{page}” is not available for path “{effectiveCustomPath ?? 'default'}”.
                  </div>
                )}
              </div>
            </ScaleToFit>
          </div>
        </ResponsiveTierProvider>
      </div>
    )
  }

  // OUTER host — scale an <iframe> whose OWN viewport equals the design canvas, so
  // `dvh`/`vh` inside are immune to the browser window size and zoom level (the sim
  // fidelity fix). The panel + badge live out here, unscaled, and drive the iframe
  // through the URL (changing a control updates `embeddedSrc`, reloading the frame).
  return (
    <div className="relative w-screen overflow-hidden bg-[#101010]" style={{ height: '100dvh' }}>
      <div className="relative h-full min-h-0 w-full min-w-0 overflow-hidden">
        <ScaleToFit
          screenSize={viewport}
          backgroundColor={config?.theme?.backgroundColor || 'white'}
          blurBackgroundColor="#1D1D1D"
        >
          <iframe
            title="Sim viewport"
            src={embeddedSrc}
            className="block border-0 bg-white"
            style={{ width: viewport.width, height: viewport.height }}
          />
        </ScaleToFit>
      </div>

      <SimBadge />
      {showPanel && (
        <SimPanel
          key={cfgParam ?? ''}
          page={page}
          pathParam={pathParam}
          scenarioParam={scenarioParam}
          vwParam={vwParam}
          expiresInParam={expiresInParam}
          photosParam={photosParam}
          filtersParam={filtersParam}
          framesParam={framesParam}
          frameAspectParam={frameAspectParam}
          stripAspectParam={stripAspectParam}
          camOn={camOn}
          cfgParam={cfgParam}
          effectiveConfig={config}
          effectiveViewport={viewport}
          onUpdate={update}
          onReseed={() => setSeedNonce((n) => n + 1)}
          projectId={routeParam || ''}
        />
      )}
    </div>
  )
}

export default SimHost
